import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Skeleton } from "@/components/ui/skeleton";
import { 
  Heart, 
  Trash2, 
  Bed, 
  Bath, 
  Ruler,
  MapPin,
  ExternalLink
} from "lucide-react";
import { useSavedProperties } from "@/hooks/use-saved-properties";
import { MockProperty } from "@/data/mockProperties";
import { cn } from "@/lib/utils";
import { formatDistanceToNow } from "date-fns";

interface SavedPropertiesDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onPropertyClick?: (property: MockProperty) => void;
}

export function SavedPropertiesDrawer({
  open,
  onOpenChange,
  onPropertyClick
}: SavedPropertiesDrawerProps) {
  const { savedProperties, isLoading, unsaveProperty } = useSavedProperties();
  // Favourite = heart-saved only (no pipeline yet). Once a row gets a
  // pipeline_id it's tracked in the kanban and shouldn't appear here.
  const favouriteProperties = savedProperties.filter((p) => !p.pipeline_id);

  const formatCurrency = (value: number) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);

  const handlePropertyClick = (property: MockProperty) => {
    onPropertyClick?.(property);
    onOpenChange(false);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-lg p-0">
        <SheetHeader className="px-6 py-4 border-b border-border">
          <SheetTitle className="flex items-center gap-2">
            <Heart className="h-5 w-5 text-destructive fill-destructive" />
            Saved Properties
            <Badge variant="secondary">{favouriteProperties.length}</Badge>
          </SheetTitle>
        </SheetHeader>

        <ScrollArea className="h-[calc(100vh-80px)]">
          <div className="p-4 space-y-3">
            {isLoading ? (
              // Loading skeletons
              Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="flex gap-3 p-3 rounded-lg border border-border">
                  <Skeleton className="w-24 h-20 rounded-md shrink-0" />
                  <div className="flex-1 space-y-2">
                    <Skeleton className="h-4 w-24" />
                    <Skeleton className="h-3 w-full" />
                    <Skeleton className="h-3 w-2/3" />
                  </div>
                </div>
              ))
            ) : favouriteProperties.length === 0 ? (
              <div className="text-center py-12">
                <Heart className="h-12 w-12 text-muted-foreground/50 mx-auto mb-4" />
                <p className="text-muted-foreground">No saved properties yet</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Click the heart icon on properties to save them here
                </p>
              </div>
            ) : (
              favouriteProperties.map((saved) => {
                const property = saved.property_data;
                return (
                  <div 
                    key={saved.id} 
                    className="flex gap-3 p-3 rounded-lg border border-border bg-card hover:bg-muted/50 transition-colors group"
                  >
                    {/* Thumbnail */}
                    <div 
                      className="w-24 h-20 rounded-md overflow-hidden shrink-0 cursor-pointer"
                      onClick={() => handlePropertyClick(property)}
                    >
                      <img 
                        src={property.imageUrl} 
                        alt={property.address}
                        className="w-full h-full object-cover"
                      />
                    </div>

                    {/* Details */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <p 
                          className="font-semibold text-foreground cursor-pointer hover:text-primary transition-colors"
                          onClick={() => handlePropertyClick(property)}
                        >
                          {formatCurrency(property.price)}
                        </p>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-destructive"
                          onClick={() => unsaveProperty(property.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                      
                      <p 
                        className="text-sm text-muted-foreground truncate cursor-pointer"
                        onClick={() => handlePropertyClick(property)}
                      >
                        {property.address}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {property.city}, {property.state} {property.zip}
                      </p>

                      {/* Property specs */}
                      <div className="flex items-center gap-3 mt-1.5 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Bed className="h-3 w-3" />
                          {property.bedrooms}
                        </span>
                        <span className="flex items-center gap-1">
                          <Bath className="h-3 w-3" />
                          {property.bathrooms}
                        </span>
                        <span className="flex items-center gap-1">
                          <Ruler className="h-3 w-3" />
                          {property.sqft.toLocaleString()}
                        </span>
                      </div>

                      {/* Saved time */}
                      <p className="text-xs text-muted-foreground/70 mt-1">
                        Saved {formatDistanceToNow(new Date(saved.created_at), { addSuffix: true })}
                      </p>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
}
